import type { NavLinksProps } from './NavLink.types';
import type { ReactElement } from 'react';

import { Box, Flex, IconButton, Popover, Text } from '@rango-dev/whale';
import { NavItem } from '@rango-dev/whale/lab';
import clsx from 'clsx';
import { Ellipsis } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { useAppSelector } from '../../hooks/reduxHooks';
import { getContainer, isActiveLink } from '../../utils/generalFunctions/common';
import { NavMoreMenu } from '../NavMoreMenu';

import styles from './NavLinks.module.css';

const MORE_ICON_SIZE = 18;
const MORE_POPOVER_SIDE_OFFSET = 8;

export function NavLinks(props: NavLinksProps): ReactElement {
  const { links, moreGroups } = props;
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { language } = useAppSelector((state) => state.settings);
  const [openMore, setOpenMore] = useState(false);

  return (
    <Flex asChild align="center" gap="5">
      <nav>
        {links.map((link) => {
          const isActive = isActiveLink(pathname, link.location, false, language);
          return (
            <NavItem
              id={link.id}
              asChild
              key={link.id}
              active={isActive}
              className={clsx(styles.navItem, isActive && styles.navItemActive, link.className)}>
              <Link id={link.id} to={link.location} onClick={link.onClick}>
                {link.title}
              </Link>
            </NavItem>
          );
        })}

        <Popover.Root open={openMore} onOpenChange={setOpenMore}>
          <Popover.Trigger>
            <IconButton
              id="header-more-icon-btn"
              aria-label={t('More')}
              variant="ghost"
              color="gray"
              radius="full"
              size="3"
              highContrast={openMore}
              className={styles.moreButton}>
              <Ellipsis size={MORE_ICON_SIZE} />
              <Box className={styles.moreBadge}>
                <Text size="1" className="beamerTrigger" />
              </Box>
            </IconButton>
          </Popover.Trigger>
          <Popover.Content
            forceMount
            align="start"
            sideOffset={MORE_POPOVER_SIDE_OFFSET}
            container={getContainer()}
            className={styles.morePopover}>
            <NavMoreMenu groups={moreGroups} onNavigate={() => setOpenMore(false)} />
          </Popover.Content>
        </Popover.Root>
      </nav>
    </Flex>
  );
}
